import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

// Layout components
import Layout from '../../../components/layout/Layout';
import Section from '../../../components/layout/Section';
import Container from '../../../components/layout/Container';

// UI Components
import Card from '../../../components/ui/Card';
import Button from '../../../components/ui/Button';
import FeatureCard from '../../../components/ui/FeatureCard';
import { FAQList } from '../../../components/ui/FAQ';
import Hero from '../../../components/sections/Hero';
import CallToAction from '../../../components/sections/CallToAction';

// Data
import { caseStudies, faqs } from '../../../data/mockData';

/**
 * Retail Industry Solutions Page
 */
const RetailIndustryPage: React.FC = () => {
  // Filter relevant case studies for the retail industry
  const relevantCaseStudies = caseStudies.filter(
    (study) => study.industry === 'Retail'
  ).slice(0, 2);

  // Filter relevant FAQs
  const relevantFaqs = faqs.slice(0, 3);

  return (
    <Layout>
      <Hero
        title="AI Solutions for Retail"
        subtitle="Transform customer experiences, optimize inventory, and increase sales with AI-powered retail solutions."
        ctaText="Schedule a Consultation"
        ctaLink="/contact"
        backgroundType="gradient"
      />

      {/* Key Challenges Section */}
      <Section bgColor="bg-white" padding="lg">
        <Container>
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12">Industry Challenges We Solve</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <Card>
                <h3 className="text-xl font-semibold mb-3">Customer Personalization</h3>
                <p className="text-neutral-700 mb-4">
                  Today's consumers expect personalized shopping experiences. Our AI solutions help you understand customer preferences 
                  and behavior to deliver tailored product recommendations and marketing messages at scale.
                </p>
              </Card>
              
              <Card>
                <h3 className="text-xl font-semibold mb-3">Inventory Management</h3>
                <p className="text-neutral-700 mb-4">
                  Balancing inventory levels to meet demand without overstocking is a constant challenge. Our AI forecasting tools
                  predict sales trends with remarkable accuracy, reducing stockouts and excess inventory.
                </p>
              </Card>
              
              <Card>
                <h3 className="text-xl font-semibold mb-3">Competitive Pricing</h3>
                <p className="text-neutral-700 mb-4">
                  Finding the optimal price point that maximizes margins while remaining competitive is complex. Our dynamic
                  pricing models analyze market conditions and competitor behavior to recommend ideal pricing strategies.
                </p>
              </Card>
              
              <Card>
                <h3 className="text-xl font-semibold mb-3">Supply Chain Optimization</h3>
                <p className="text-neutral-700 mb-4">
                  Disruptions and inefficiencies in the supply chain lead to increased costs and customer disappointment.
                  Our supply chain AI solutions identify potential issues before they impact your business.
                </p>
              </Card>
            </div>
          </div>
        </Container>
      </Section>
      
      {/* Our Solutions Section */}
      <Section bgColor="bg-neutral-50" padding="lg">
        <Container>
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12">Our Retail Solutions</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <FeatureCard
                title="Customer Behavior Analytics"
                description="Advanced AI analytics that identify patterns in shopper behavior to create hyper-personalized experiences and targeted marketing campaigns."
                icon={
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                }
              />
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <FeatureCard
                title="Intelligent Inventory Management"
                description="Predictive analytics that forecast demand with up to 95% accuracy, reducing stock issues while optimizing inventory investment."
                icon={
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                  </svg>
                }
              />
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <FeatureCard
                title="Dynamic Pricing Engine"
                description="Automated pricing optimization that adjusts to market conditions, competitors, and inventory levels in real-time."
                icon={
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                }
              />
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <FeatureCard
                title="Omnichannel Customer Service"
                description="AI-powered customer service solutions that provide seamless support across in-store, online, and mobile channels."
                icon={
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                }
              />
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              <FeatureCard
                title="Supply Chain Intelligence"
                description="End-to-end visibility and predictive analytics that optimize your supply chain from manufacturer to customer."
                icon={
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                  </svg>
                }
              />
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.5 }}
            >
              <FeatureCard
                title="In-Store Analytics"
                description="Computer vision and sensor-based analytics that provide insights into customer traffic patterns and behavior within physical stores."
                icon={
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                }
              />
            </motion.div>
            </div>

            <div className="text-center">
              <Button
                variant="primary"
                size="lg"
                onClick={() => window.location.href = '/services'}
              >
                Explore All Our Services
              </Button>
            </div>
          </div>
        </Container>
      </Section>
      
      {/* Case Studies Section */}
      {relevantCaseStudies.length > 0 && (
        <Section bgColor="bg-white" padding="lg">
          <Container>
            <div className="max-w-5xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-12">Success Stories in Retail</h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {relevantCaseStudies.map((study, index) => (
                  <motion.div
                    key={study.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="flex"
                  >
                    <Link to={study.url} className="w-full h-full">
                      <Card hover className="h-full flex flex-col">
                        <div className="aspect-video bg-neutral-200 rounded-t-lg overflow-hidden">
                          <img
                            src={study.imageSrc}
                            alt={study.title}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              const img = e.target as HTMLImageElement;
                              img.style.display = 'none';
                              if (img.nextElementSibling) {
                                (img.nextElementSibling as HTMLElement).style.display = 'flex';
                              }
                            }}
                          />
                          <div
                            className="w-full h-full bg-gradient-to-br from-primary to-secondary opacity-70 flex items-center justify-center hidden"
                            style={{ display: 'flex' }}
                          >
                            <span className="text-white text-lg font-semibold">{study.industry}</span>
                          </div>
                        </div>

                        <div className="p-6 md:p-8 flex-grow flex flex-col">
                          <div className="mb-3">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
                              {study.solution}
                            </span>
                          </div>

                          <h3 className="text-xl font-semibold mb-3">{study.title}</h3>
                          <p className="text-neutral-600 mb-4 text-sm">{study.excerpt}</p>

                          <div className="mt-auto pt-2 border-t border-neutral-100">
                            <span className="inline-flex items-center text-primary font-medium text-sm">
                              Read full case study
                              <svg className="ml-2 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                              </svg>
                            </span>
                          </div>
                        </div>
                      </Card>
                    </Link>
                  </motion.div>
                ))}
              </div>

              <div className="text-center mt-10">
                <Link to="/case-studies" className="inline-flex items-center text-primary font-medium">
                  View all case studies
                  <svg className="ml-2 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </Link>
              </div>
            </div>
          </Container>
        </Section>
      )}
      
      {/* FAQ Section */}
      <Section bgColor="bg-neutral-50" padding="lg">
        <Container>
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12">Frequently Asked Questions</h2>
            <FAQList faqs={relevantFaqs} />
            
            <div className="mt-10 text-center">
              <Link to="/contact" className="inline-flex items-center text-primary font-medium">
                Have more questions? Contact our retail AI specialists
                <svg className="ml-2 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </Link>
            </div>
          </div>
        </Container>
      </Section>
      
      {/* Call to Action */}
      <CallToAction
        title="Ready to revolutionize your retail business with AI?"
        description="Schedule a consultation to discuss how our AI solutions can help your retail business improve customer experiences, optimize operations, and increase revenue."
        ctaText="Schedule a Consultation"
        ctaLink="/contact"
      />
    </Layout>
  );
};

export default RetailIndustryPage;